import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import VideoCard from "@/components/feature/VideoCard";
import SeoMeta from "@/components/feature/SeoMeta";
import { categories, allVideos } from "@/mocks/videos";

const catMeta: Record<string, { desc: string }> = {
  mingxing: { desc: "一线明星动态与圈内新鲜事，一手吃瓜现场，覆盖人气明星的出行、互动与最新动向。" },
  zongyi: { desc: "热门综艺高能片段与嘉宾互动名场面合集，笑点泪点都在这里。" },
  resou: { desc: "每日热搜话题深度盘点与事件时间线梳理，理性吃瓜不迷路。" },
  hongtan: { desc: "各大盛典红毯造型盘点与高光时刻直击，穿搭灵感一站看全。" },
  yinyue: { desc: "演唱会与舞台表演名场面，live 高能直拍，感受现场音乐的魅力。" },
  quannei: { desc: "娱乐圈深度解读与幕后故事，理性吃瓜观察，看见行业真实面。" },
};

export default function CategoryPage() {
  const { id } = useParams();
  const cat = categories.find((c) => c.id === id);
  const list = allVideos.filter((v) => v.category === id);

  if (!cat) {
    return (
      <div className="bg-background-50 min-h-screen">
        <SiteHeader />
        <div className="mx-auto max-w-7xl px-4 md:px-6 pt-32 pb-24 text-center">
          <h1 className="text-2xl font-black text-foreground-950">分类不存在</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 font-semibold">
            返回首页
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="bg-background-50 min-h-screen">
      <SeoMeta
        title={`${cat.name}吃瓜视频 - 国产吃瓜视频合集精选`}
        description={`${catMeta[id as string]?.desc || cat.desc} 聚合全网${cat.name}吃瓜视频合集、精选与大全内容，按热度实时更新，一次看全国产吃瓜视频热门精选与推荐。`}
        type="collection"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${cat.name}吃瓜视频`,
            description: catMeta[id as string]?.desc || cat.desc,
            hasPart: list.map((v) => ({
              "@type": "VideoObject",
              name: v.title,
              description: v.desc,
              duration: `PT${v.duration.replace(":", "M")}S`,
              url: `https://www.readdy.ai/video/${v.id}`,
            })),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://www.readdy.ai/" },
              { "@type": "ListItem", position: 2, name: cat.name },
            ],
          },
        ]}
      />
      <SiteHeader />

      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={cat.cover} alt={cat.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-6 pt-32 pb-16 md:pt-40 md:pb-20 w-full">
          <nav className="flex items-center gap-1 text-sm text-background-50/80 mb-5 flex-wrap">
            <Link to="/" className="hover:text-background-50">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50">{cat.name}</span>
          </nav>
          <h1 className="font-heading text-3xl md:text-5xl font-black text-background-50">
            {cat.name} <span className="text-accent-400">视频</span>
          </h1>
          <p className="mt-3 max-w-2xl text-background-50/85">{catMeta[id as string]?.desc || cat.desc}</p>
          <span className="mt-5 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-background-50/15 border border-background-50/30 text-background-50 text-sm">
            <i className="ri-play-circle-line"></i> 共 {cat.count} 个视频
          </span>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">精选内容</h2>
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/category/${c.id}`}
                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                  c.id === id
                    ? "bg-primary-500 text-background-50 font-bold"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>

        {list.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {list.map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
        ) : (
          <p className="text-foreground-500 text-center py-16">
            该分类内容整理中，敬请期待。前往其他分类查看更多吃瓜视频。
          </p>
        )}

        {/* 相关推荐 */}
        <div className="mt-12 bg-background-100 rounded-2xl p-6 md:p-8">
          <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950 mb-5">其他热门分类</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {categories
              .filter((c) => c.id !== id)
              .slice(0, 4)
              .map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.id}`}
                  className="group relative rounded-xl overflow-hidden h-28 flex items-end"
                >
                  <img
                    src={c.cover}
                    alt={c.name}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <span className="relative p-4 text-background-50 font-bold">{c.name}</span>
                </Link>
              ))}
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}